import React, { useEffect, useState, useContext } from "react";
import { UserContext } from "../../../../context/UserContext"; // Import User Context

import CompanyLayout from "../../../layout/CompanyLayout";
import { getCurrentUser } from "../../../../api/user.api";
import {
  deleteCampaign,
  getCampaignsByCompany,
} from "../../../../api/campaigns.api";
import { baseURL } from "../../../../api/api";
import { Link } from "react-router-dom";
import { BsTrash3 } from "react-icons/bs";
import { MoonLoader } from "react-spinners"; // Import MoonLoader
import {
  confirmCampaignDelete,
  errorMsg,
  success,
} from "../../../../utils/notifications";
import { FiPlus } from "react-icons/fi";

function Campaigns() {
  const [campaignDetails, setCampaignDetails] = useState([]);
  const [companyRegistered, setCompanyRegistered] = useState(true); // State to track if the company is registered
  const [loading, setLoading] = useState(true); // State to track loading state
  const { user } = useContext(UserContext); // Get user from context

  useEffect(() => {
    getCurrentUser()
      .then((res) => {
        const companyId = res.data?.user?.companyId;

        if (!companyId) {
          setCompanyRegistered(false); // If no company ID, set to false
          setLoading(false); // Stop loading if no company
          return;
        }

        getCampaignsByCompany(companyId)
          .then((res) => {
            setCampaignDetails(res?.data);
            setLoading(false); // Stop loading after data is fetched
          })
          .catch((error) => {
            console.error("Error fetching data:", error);
            setLoading(false); // Stop loading if there is an error
          });
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoading(false); // Stop loading if there is an error
      });
  }, []);

  const handleDelete = (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      confirmCampaignDelete().then(async (result) => {
        if (result.isConfirmed) {
          await deleteCampaign(id);
          setCampaignDetails(
            campaignDetails.filter((campaign) => campaign._id !== id)
          );
          await success("Success!", "Campaign deleted successfully!");
        }
      });
    } catch (error) {
      console.error("Error deleting campaign:", error);
      errorMsg("Oops", error?.response?.message);
    }
  };

  const fetchUserAndCampaigns = async () => {
    try {
      const res = await getCurrentUser();
      const companyId = res.data?.user?.companyId;

      if (!companyId) {
        setCompanyRegistered(false);
        setLoading(false);
        return;
      }

      try {
        const campaignRes = await getCampaignsByCompany(companyId);
        setCampaignDetails(campaignRes?.data);
      } catch (error) {
        console.error("Error fetching campaign data:", error);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CompanyLayout title="Campaigns" profileDetails={user}>
      <div className="p-5">
        <div className=" flex items-center justify-end sticky top-16 bg-slate-50 py-3 z-10 px-2">
          <Link
            to="/campaigns/add"
            className="flex items-center justify-center rounded-md bg-yellow px-3 py-2.5 text-base font-semibold text-gray-700 shadow-sm hover:bg-opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellowColor"
          >
            {" "}
            <FiPlus className="text-lg text-gray-700" />
            Add Campaign
          </Link>
        </div>
        {/* Show a loader while the data is being fetched */}
        {loading ? (
          <div className="text-center py-5 flex items-center justify-center">
            <MoonLoader color="black" size={40} />
          </div>
        ) : !companyRegistered ? (
          <div className="text-center py-5">
            <p className="text-xl text-red-600 font-semibold">
              Missing Company Profile. Please register your company first.
            </p>
          </div>
        ) : campaignDetails.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-xl text-yellow-600 font-semibold">
              No campaigns found. Please add a campaign.
            </p>
          </div>
        ) : (
          <div className="gap-7 grid grid-cols-12 mt-16">
            {campaignDetails.map((campaign, index) => (
              <Link
                to={`/campaigndetails/${campaign._id}`}
                key={index}
                className="lg:col-span-4 md:col-span-12 col-span-12"
              >
                <div className="bg-white border border-gray-200 rounded-lg shadow">
                  <div className="px-5 py-3 flex items-center justify-end">
                    <button
                      id="delCampaign"
                      onClick={(e) => handleDelete(campaign._id, e)}
                      className="ml-3 text-xl h-8 w-8 flex items-center justify-center bg-red-100 text-red-700  px-0.5 rounded-md"
                    >
                      <BsTrash3 />
                    </button>
                  </div>
                  <video
                    className="hide-voice-control object-cover h-64 w-full"
                    alt=""
                    muted
                    controls
                  >
                    <source
                      src={`${baseURL}/s3/images/${campaign?.video?.name}`}
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                  <div className="py-5">
                    <div className="px-5 flex items-center justify-between">
                      <p className="text-xl font-semibold text-gray-700">
                        {campaign.name}
                      </p>
                      <p className="text-lg font-normal text-gray-600">
                        € {campaign?.totalCost}
                      </p>
                    </div>
                    <div className="px-5 flex items-center justify-between">
                      <p className="text-base font-semibold text-gray-600">
                        Location
                      </p>
                      <p className="text-sm font-normal text-gray-600">
                        {campaign?.location}
                      </p>
                    </div>
                    <div className="px-5 flex justify-between my-3">
                      <h1 className="text-gray-600 font-semibold text-base">
                        Campaign Status
                      </h1>
                      <span
                        className={`px-2 py-0.5 rounded ${
                          campaign?.status === "Pending Approval"
                            ? "bg-green-100 text-green-700"
                            : campaign?.status === "Approved"
                            ? "bg-orange-100 text-orange-700"
                            : "bg-amber-100 text-amber-800"
                        }  text-sm`}
                      >
                        {campaign?.status}
                      </span>
                    </div>
                    <div className="px-5 flex items-center justify-between">
                      <p className="text-base font-semibold text-gray-600">
                        Date
                      </p>
                      <p className="text-sm font-normal text-gray-600">
                        {campaign?.startDate.split("T")[0]} -{" "}
                        {campaign?.endDate.split("T")[0]}
                      </p>
                    </div>
                    <div className="px-5 flex items-center justify-between">
                      <p className="text-base font-semibold text-gray-600">
                        Number of Weeks
                      </p>
                      <p className="text-sm font-normal text-gray-600">
                        {campaign?.numberOfWeeks}
                      </p>
                    </div>

                    <div className="px-5 flex items-center justify-between">
                      <p className="text-base font-semibold text-gray-600">
                        Number of Screens
                      </p>
                      <p className="text-sm font-normal text-gray-600">
                        {campaign?.numberOfScreens}
                      </p>
                    </div>
                    <div className="px-5 flex items-center justify-between">
                      <p className="text-base font-semibold text-gray-600">
                        Paid {campaign?.paid}
                      </p>
                      <p
                        className={`text-sm font-medium px-2 py-0.5 rounded ${
                          campaign?.paid
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {campaign?.paid ? "Yes" : "No"}
                      </p>
                    </div>
                    <div className="px-5 flex items-center justify-between">
                      <p className="text-base font-semibold text-gray-600">
                        Impressions
                      </p>
                      <p className="text-sm font-normal text-gray-600">
                        {campaign?.impressions}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </CompanyLayout>
  );
}

export default Campaigns;
